
import { useEffect, useState } from 'react';

import { TagsPicker } from './TagsPicker';
import { Button, Header, Message } from 'semantic-ui-react'

import { useQuery, gql, useMutation } from '@apollo/client';

import { GET_TAGS as GET_POSSIBLE_TAGS } from '../../queries/tags';

/**
 * Phase 7.1 — the permissions editor, rebuilt on grants.
 *
 * It used to read `visibilityFormula` / `editFormula` and write them back. Phase 4 moved every
 * reader onto grants, so by then those columns were consulted by nothing: an admin could change
 * someone's permissions, watch it save, and change nothing at all.
 *
 * The shape on screen is deliberately unchanged. A formula was tag intersections unioned —
 * `(1x3)+(2x4)` meaning "tags 1 and 3, or tags 2 and 4" — and a dynamic grant is exactly that,
 * so the two lists of tag pickers still say what they always said. What changed is that saving
 * now alters access.
 */

const GET_DIRECT_ACCESS = gql`
    query PersonDirectAccess($personId: Int!) {
        user(personId: $personId) {
            directAccess {
                visibility
                editing
                hasAccessNotShown
            }
        }
    }
`;

const SET_PERSON_ACCESS = gql`
    mutation SetPersonAccess($userId: Int!, $visibility: [[Int!]!]!, $editing: [[Int!]!]!) {
        setPersonAccess(userId: $userId, visibility: $visibility, editing: $editing) {
            status
        }
    }
`;

const PermissionEditor = ({ userId, refetchPerson }: { userId: number, refetchPerson: any }) => {
    const [visibility, setVisibility] = useState<number[][]>([]);
    const [editing, setEditing] = useState<number[][]>([]);
    const [saved, setSaved] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const [setPersonAccess, { loading: saving }] = useMutation(SET_PERSON_ACCESS);

    const { loading, error, data } = useQuery(GET_POSSIBLE_TAGS, {
        variables: { showPeopleTags: true, showCourseTags: true, includeAllTags: true },
    });

    const { data: accessData, refetch } = useQuery(GET_DIRECT_ACCESS, {
        variables: { personId: Number(userId) },
        fetchPolicy: 'network-only',
    });

    const access = accessData?.user?.directAccess;

    useEffect(() => {
        if (access) {
            setVisibility(access.visibility.map((s: number[]) => [...s]));
            setEditing(access.editing.map((s: number[]) => [...s]));
        }
    }, [access]);

    // `directAccess` resolves to null for anyone without authority to change access — the same
    // `mayGrant` test the mutation enforces. Gating on the server's answer rather than on a
    // local `useIsAdmin()` keeps the button and the mutation from drifting apart, which is what
    // Phase 9 just separated: seeing everything and deciding who sees everything are different
    // jobs, and only the second one belongs here.
    if (!access) {
        return null;
    }

    const section = (
        label: string,
        sets: number[][],
        setSets: (next: number[][]) => void,
    ) => (
        <>
            <Header as='h4'>{label}</Header>
            {sets.length === 0 && (
                <p style={{ color: '#767676', margin: '0 0 8px' }}>
                    Nothing — they reach nobody through this screen.
                </p>
            )}
            {sets.map((set, index) => (
                <div style={{ display: 'flex' }} key={index}>
                    <TagsPicker
                        tags={set.map((id) => ({ id }))}
                        refetchParent={refetchPerson}
                        loading={loading}
                        error={error}
                        data={data}
                        updateTags={({ variables }: { variables: any }) => {
                            const next = sets.map((s, i) => (i === index ? variables.tagIds : s));
                            setSets(next);
                        }}
                        payload={{ userId: Number(userId) }}
                    />
                    <Button
                        onClick={() => setSets(sets.filter((_, i) => i !== index))}
                    >
                        -
                    </Button>
                </div>
            ))}
            <Button onClick={() => setSets([...sets, []])}>+</Button>
        </>
    );

    return (
        <>
            <Header>Permissions</Header>

            {access.hasAccessNotShown && (
                <Message warning>
                    <Message.Header>They have access this screen cannot show</Message.Header>
                    <p>
                        Some of what they can reach comes from a role, or from access granted on
                        one specific thing. Clearing the lists below will not remove it.
                    </p>
                </Message>
            )}

            {section('What they can see:', visibility, setVisibility)}
            {section('What they can edit:', editing, setEditing)}

            {saveError && <Message negative>{saveError}</Message>}
            {saved && <Message positive>Permissions updated.</Message>}

            <Button
                primary
                loading={saving}
                disabled={saving}
                onClick={async () => {
                    setSaved(false);
                    setSaveError(null);

                    try {
                        await setPersonAccess({
                            variables: {
                                userId: Number(userId),
                                // Unfinished rows are dropped server-side too; doing it here as
                                // well keeps the request honest about what is being asked for.
                                visibility: visibility.filter((s) => s.length > 0),
                                editing: editing.filter((s) => s.length > 0),
                            },
                        });

                        // Refetch rather than trusting local state: the server drops empty and
                        // duplicate sets, so what it stored may differ from what was on screen,
                        // and a permissions editor showing something other than the truth is the
                        // exact failure this rewrite exists to end.
                        await refetch();
                        setSaved(true);
                    } catch (e: any) {
                        setSaveError(e?.message ?? 'Could not update permissions.');
                    }
                }}
            >
                Save
            </Button>
        </>
    )
}

export { PermissionEditor }
